import React, { JSX, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { JSONViewer } from './components/JSONViewer';

interface CustomMetric {
  path: string[];
  label: string;
  value: any;
  color: string;
}

interface IsilonDocData {
  [key: string]: any;
}

interface SavedDoc {
  id: number;
  date: string;
  filename: string;
  displayName: string;
  data: IsilonDocData;
  customMetrics?: CustomMetric[];
}

interface IsilonDocAnalyzerProps {
  docData: IsilonDocData | null;
  setDocData: (data: IsilonDocData | null) => void;
  savedDocs: SavedDoc[];
  setSavedDocs: (docs: SavedDoc[]) => void;
  docName: string;
  setDocName: (name: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  error: string | null;
  setError: (error: string | null) => void;
  showRawData: boolean;
  setShowRawData: (show: boolean) => void;
}

const countProperties = (obj: any): number => {
  let count = 0;
  for (const key in obj) {
    count++;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      count += countProperties(obj[key]);
    }
  }
  return count;
};

// Hilfsfunktion zum sicheren Extrahieren von Werten
const safeExtract = (obj: any, path: string[], defaultValue: any = 'N/A') => {
  let current = obj;
  for (const key of path) {
    if (current === null || current === undefined || current === 'N/A' || current === 'null') {
      return defaultValue;
    }
    current = current[key];
  }
  return current === null || current === undefined || current === 'N/A' || current === 'null' 
    ? defaultValue 
    : current;
};

const IsilonDocAnalyzer: React.FC<IsilonDocAnalyzerProps> = ({
  docData,
  setDocData,
  savedDocs,
  setSavedDocs,
  docName,
  setDocName,
  activeTab,
  setActiveTab,
  error,
  setError,
  showRawData,
  setShowRawData
}) => {
  const [customMetrics, setCustomMetrics] = useState<CustomMetric[]>([]);
  const [showMetricDialog, setShowMetricDialog] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState<any>();
  const [highlightedPath, setHighlightedPath] = useState<string[]>([]);

  // Color mapping object
  const colorHexMap: { [key: string]: string } = {
    'blue': '3B82F6',
    'green': '10B981',
    'purple': '8B5CF6',
    'orange': 'F59E0B',
    'teal': '14B8A6',
    'indigo': '6366F1',
    'pink': 'EC4899',
    'amber': 'F59E0B',
    'red': 'EF4444',
    'yellow': 'EAB308',
    'cyan': '06B6D4',
    'lime': '84CC16',
    'emerald': '10B981',
    'violet': '8B5CF6',
    'fuchsia': 'D946EF',
    'rose': 'F43F5E'
  };

  // Available colors for metrics
  const metricColors = [
    'blue', 'green', 'purple', 'orange', 'teal', 'indigo', 'pink', 'amber',
    'red', 'yellow', 'cyan', 'lime', 'emerald', 'violet', 'fuchsia', 'rose'
  ].map(color => `#${colorHexMap[color]}`);

  const handleSelect = (path: string[], value: any, isCount: boolean = false) => {
    console.log('handleSelect called:', { path, value, isCount });
    
    if (isCount) {
      // Get the current property name
      let label = 'Count';
      
      if (path && path.length > 0) {
        const propertyName = path[path.length - 1];
        if (propertyName) {
          // Format the property name
          label = propertyName
            .split(/(?=[A-Z])|_/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
      }
      
      const color = metricColors[customMetrics.length % metricColors.length];
      
      const newMetric: CustomMetric = {
        path,
        label,
        value,
        color
      };
      
      const updatedMetrics = [...customMetrics, newMetric];
      setCustomMetrics(updatedMetrics);
      
      // Save metrics to localStorage with docData id if available
      if (docData) {
        localStorage.setItem(`doc_metrics_${docData.id}`, JSON.stringify(updatedMetrics));
      }
    } else {
      setSelectedPath(path);
      setSelectedValue(value);
      setShowMetricDialog(true);
    }
  };

  const addCustomMetric = (path: string[], value: any) => {
    const label = path[path.length - 1];
    const color = metricColors[customMetrics.length % metricColors.length];
    
    const newMetric: CustomMetric = {
      path,
      label,
      value,
      color
    };

    const updatedMetrics = [...customMetrics, newMetric];
    setCustomMetrics(updatedMetrics);
    setShowMetricDialog(false);
    setSelectedPath([]);

    // Save to localStorage if we have a current doc
    if (docData) {
      localStorage.setItem(`doc_metrics_${docData.id}`, JSON.stringify(updatedMetrics));
    }
  };

  const removeCustomMetric = (path: string[]) => {
    setCustomMetrics(customMetrics.filter(metric => 
      !arraysEqual(metric.path, path)
    ));
  };

  const arraysEqual = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      console.log('Parsed JSON data:', data);
      setDocData(data);
      setError(null);
    } catch (error) {
      console.error('Error parsing JSON file:', error);
      setError('Error parsing the JSON file. Please make sure it is a valid JSON file.');
      setDocData(null);
    }
  };

  const saveDocsToFile = (docs: SavedDoc[]) => {
    const jsonString = JSON.stringify(docs, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'saved_docs.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveDoc = () => {
    if (!docData) return;

    const newDoc: SavedDoc = {
      id: Date.now(),
      date: new Date().toLocaleString(),
      filename: docName.trim() || 'Unnamed Document',
      displayName: docName.trim() || 'Unnamed Document',
      data: docData,
      customMetrics
    };

    const updatedDocs = [newDoc, ...savedDocs].slice(0, 10);
    setSavedDocs(updatedDocs);
    saveDocsToFile(updatedDocs);
    setActiveTab('history');
  };

  const loadSavedDoc = (docId: number) => {
    const doc = savedDocs.find(d => d.id === docId);
    if (doc) {
      setDocData(doc.data);
      setDocName(doc.displayName);
      setCustomMetrics(doc.customMetrics || []);
      setActiveTab('overview');
    }
  };

  const deleteDoc = (docId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const updatedDocs = savedDocs.filter(d => d.id !== docId);
    setSavedDocs(updatedDocs);
    saveDocsToFile(updatedDocs);
  };

  const handleSavedFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const text = await file.text();
        const docs = JSON.parse(text);
        setSavedDocs(docs);
      } catch (e) {
        console.error("Error loading saved documents file:", e);
        alert("Error loading saved documents file. Please check the file format.");
      }
    }
  };

  const handleMetricClick = (metric: CustomMetric) => {
    setHighlightedPath(metric.path);
  };

  const renderUploadTab = () => (
    <div className="p-4 bg-white rounded shadow">
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="file-upload">
          Select Isilon Documentation File
        </label>
        <input
          id="file-upload"
          type="file"
          onChange={handleFileUpload}
          className="w-full p-2 border border-gray-300 rounded"
          accept=".json"
        />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="doc-name">
          Description (optional)
        </label>
        <input
          id="doc-name"
          type="text"
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          placeholder="e.g. Cluster Name or Description"
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      {docData && (
        <button
          onClick={handleSaveDoc}
          className="w-full p-2 bg-blue-500 text-white rounded font-bold hover:bg-blue-700"
        >
          Save Documentation
        </button>
      )}
    </div>
  );

  const renderHistoryTab = () => (
    <div className="p-4 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Saved Documents</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => saveDocsToFile(savedDocs)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Export History
          </button>
          <label className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 cursor-pointer">
            Import History
            <input
              type="file"
              accept=".json"
              onChange={handleSavedFileUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {savedDocs.length === 0 ? (
        <p>No saved documents available.</p>
      ) : (
        <div className="space-y-2">
          {savedDocs.map((doc) => {
            const clusterName = doc.data?.config?.cluster?.name || 'N/A';
            const clusterGuid = doc.data?.config?.cluster?.guid || 'N/A';
            const clusterSwid = doc.data?.config?.cluster?.swid || 'N/A';
            
            return (
              <div
                key={doc.id}
                className="p-3 border rounded hover:bg-gray-100 cursor-pointer"
                onClick={() => loadSavedDoc(doc.id)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-grow">
                    <div className="font-medium">{doc.displayName}</div>
                    <div className="text-sm text-gray-600">
                      <div>Cluster: {clusterName}</div>
                      <div>GUID: {clusterGuid}</div>
                      <div>SWID: {clusterSwid}</div>
                    </div>
                    <div className="text-sm text-gray-500">{doc.date}</div>
                  </div>
                  <button
                    onClick={(e) => deleteDoc(doc.id, e)}
                    className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderOverview = () => {
    if (!docData) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customMetrics.map((metric, index) => (
            <div
              key={index}
              className="p-4 rounded-lg shadow-md border cursor-pointer hover:shadow-lg transition-shadow"
              style={{ borderColor: metric.color }}
              onClick={() => handleMetricClick(metric)}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">{metric.label}</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCustomMetric(metric.path);
                  }}
                  className="text-gray-500 hover:text-red-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <p className="text-2xl font-bold mt-2">{metric.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4">JSON Data Explorer</h2>
          <p className="text-sm text-gray-600 mb-2">Click on any value to add it to the metrics above</p>
          <JSONViewer
            data={docData}
            onSelect={handleSelect}
            highlightPath={highlightedPath}
          />
        </div>

        {showMetricDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Metric</h3>
              <p className="mb-4">
                Add "{selectedPath.length > 0 ? selectedPath[selectedPath.length - 1] : ''}" ({selectedValue}) to your metrics?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  onClick={() => {
                    setShowMetricDialog(false);
                    setSelectedPath([]);
                    setSelectedValue(undefined);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => {
                    if (selectedPath.length > 0) {
                      const propertyName = selectedPath[selectedPath.length - 1];
                      const formattedLabel = propertyName
                        .split(/(?=[A-Z])|_/)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');
                      
                      const color = metricColors[customMetrics.length % metricColors.length];
                      
                      const newMetric: CustomMetric = {
                        path: selectedPath,
                        label: formattedLabel,
                        value: selectedValue,
                        color
                      };
                      
                      const updatedMetrics = [...customMetrics, newMetric];
                      setCustomMetrics(updatedMetrics);
                      
                      if (docData) {
                        localStorage.setItem(`doc_metrics_${docData.id}`, JSON.stringify(updatedMetrics));
                      }
                    }
                    setShowMetricDialog(false);
                    setSelectedPath([]);
                    setSelectedValue(undefined);
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4">Isilon/PowerScale Documentation Analyzer</h2>
        
        {/* Tabs */}
        <div className="flex border-b mb-6">
          <button
            className={`py-2 px-4 mr-2 ${activeTab === 'upload' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload
          </button>
          <button
            className={`py-2 px-4 mr-2 ${activeTab === 'history' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button
            className={`py-2 px-4 ${activeTab === 'overview' ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
            onClick={() => setActiveTab('overview')}
            disabled={!docData}
          >
            Overview
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'upload' && renderUploadTab()}
        {activeTab === 'history' && renderHistoryTab()}
        {activeTab === 'overview' && renderOverview()}
      </div>
    </div>
  );
};

export default IsilonDocAnalyzer; 